/**
 * AddFormEmbedModal
 *
 * Adds a third-party form (Jotform, Google Forms, Sprout Studio) as a section.
 *
 * The paste is sent to the worker, which extracts the provider and form id and
 * hands back a descriptor. THAT is what gets stored — the pasted markup is
 * discarded and never reaches a published page. So this screen shows what was
 * detected before anything is added: staff confirm a provider and a form id,
 * not a blob of HTML they cannot read.
 *
 * Two steps on purpose. Parsing is free and reversible; adding a section is
 * neither, and a wrong form on a live site is the mistake this prevents.
 */

import { useState, useCallback } from 'react';
import { Loader2, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { parseFormEmbedCode, type FormEmbedDescriptor } from '../../api/editor';
import { applyStructuralChanges } from '../../api/ai-editor';

interface AddFormEmbedModalProps {
  websiteId: string;
  pageSlug: string;
  onClose: () => void;
  onAdded: () => void;
}

const PROVIDER_LABELS: Record<FormEmbedDescriptor['provider'], string> = {
  jotform: 'Jotform',
  'google-forms': 'Google Forms',
  'sprout-studio': 'Sprout Studio',
};

export function AddFormEmbedModal({
  websiteId,
  pageSlug,
  onClose,
  onAdded,
}: AddFormEmbedModalProps) {
  const [embedCode, setEmbedCode] = useState('');
  const [headline, setHeadline] = useState('');
  const [detected, setDetected] = useState<FormEmbedDescriptor | null>(null);
  const [checking, setChecking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheck = useCallback(async () => {
    setChecking(true);
    setError(null);
    setDetected(null);
    try {
      const res = await parseFormEmbedCode(websiteId, embedCode);
      setDetected(res.embed);
    } catch (err) {
      // The server's message names what to do next (which provider, where to
      // find the right code). Showing it verbatim is the whole point.
      setError(err instanceof Error ? err.message : 'Could not read that embed code.');
    } finally {
      setChecking(false);
    }
  }, [websiteId, embedCode]);

  const handleAdd = useCallback(async () => {
    if (!detected) return;
    setAdding(true);
    setError(null);
    try {
      const result = await applyStructuralChanges(websiteId, [
        {
          id: crypto.randomUUID(),
          action: 'add_section',
          target: { page: pageSlug },
          // The descriptor rides along with the add so the section is never
          // stored half-configured.
          data: {
            type: 'embed',
            provider: detected.provider,
            formId: detected.formId,
            host: detected.host,
            height: detected.height,
            headline,
          },
          position: 'end',
        },
      ]);
      if (!result.success) {
        throw new Error(result.error || 'Could not add the form section.');
      }
      onAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the form section.');
    } finally {
      setAdding(false);
    }
  }, [websiteId, pageSlug, detected, headline, onAdded, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Add an embedded form</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Paste the embed code from the customer's form. We support{' '}
            <strong>Jotform</strong>, <strong>Google Forms</strong> and{' '}
            <strong>Sprout Studio</strong>. Only the
            provider and form ID are stored — the pasted code itself is discarded.
          </p>

          <div>
            <label
              htmlFor="embed-code"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Embed code
            </label>
            <textarea
              id="embed-code"
              rows={5}
              value={embedCode}
              onChange={(e) => {
                setEmbedCode(e.target.value);
                setDetected(null);
                setError(null);
              }}
              placeholder={'<iframe src="https://form.jotform.com/240123456789012" ...></iframe>'}
              className="w-full px-2.5 py-2 text-xs font-mono border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
            />
            <p className="text-xs text-gray-400 mt-1">
              In Google Forms this is <strong>Send → &lt; &gt;</strong>. In Jotform
              it is <strong>Publish → Embed</strong>. In Sprout Studio it is{' '}
              <strong>Settings → Communication → Contact Forms → Embed Code</strong>
              {' '}(the iframe). A share link works too.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {detected && (
            <div className="px-3 py-3 bg-green-50 border border-green-100 rounded">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-green-900 space-y-0.5">
                  <p className="font-medium">
                    {PROVIDER_LABELS[detected.provider] ?? detected.provider} form
                  </p>
                  <p className="font-mono text-green-800 break-all">{detected.formId}</p>
                  <p className="text-green-700">
                    Served from {detected.host} · {detected.height}px tall
                  </p>
                </div>
              </div>
            </div>
          )}

          {detected && (
            <div>
              <label
                htmlFor="embed-headline"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Heading above the form{' '}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="embed-headline"
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="New Patient Forms"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </div>
          )}

          <p className="text-xs text-gray-500">
            The form is added to the bottom of the <strong>{pageSlug}</strong> page.
            Responses go to the customer's own {detected
              ? PROVIDER_LABELS[detected.provider] ?? 'provider'
              : 'form provider'}{' '}
            account, not to the niftee leads inbox.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          {!detected ? (
            <button
              type="button"
              onClick={handleCheck}
              disabled={!embedCode.trim() || checking}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {checking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Check code
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAdd}
              disabled={adding}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40 transition-colors"
            >
              {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Add form section
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
